import { useState, useCallback } from 'react';
import { convertHeight, convertWeight } from '../utils/unitUtils';

export const useFormState = () => {
  const [ageValue, setAgeValue] = useState<string>('');
  const [height, setHeight] = useState<number | null>(null);
  const [weight, setWeight] = useState<number | null>(null);
  const [heightUnit, setHeightUnit] = useState<'cm' | 'in'>('cm');
  const [weightUnit, setWeightUnit] = useState<'kg' | 'lb'>('kg');
  const [heightInput, setHeightInput] = useState<string>('');
  const [weightInput, setWeightInput] = useState<string>('');
  const [hasSyndrome, setHasSyndrome] = useState<boolean>(false);
  const [showETTCalculations, setShowETTCalculations] = useState<boolean>(false);

  const handleHeightInputChange = useCallback((value: string) => {
    setHeightInput(value);
    if (value === '') {
      setHeight(null);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const heightInCm = heightUnit === 'cm' ? numValue : convertHeight(numValue, 'in', 'cm');
        setHeight(heightInCm);
      }
    }
  }, [heightUnit]);

  const handleWeightInputChange = useCallback((value: string) => {
    setWeightInput(value);
    if (value === '') {
      setWeight(null);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const weightInKg = weightUnit === 'kg' ? numValue : convertWeight(numValue, 'lb', 'kg');
        setWeight(weightInKg);
      }
    }
  }, [weightUnit]);

  const handleHeightUnitChange = useCallback((newUnit: 'cm' | 'in') => {
    setHeightUnit(newUnit);
    if (height !== null) {
      const convertedValue = convertHeight(height, 'cm', newUnit);
      setHeightInput(convertedValue.toString());
    }
  }, [height]);

  const handleWeightUnitChange = useCallback((newUnit: 'kg' | 'lb') => {
    setWeightUnit(newUnit);
    if (weight !== null) {
      const convertedValue = convertWeight(weight, 'kg', newUnit);
      setWeightInput(convertedValue.toString());
    }
  }, [weight]);

  const handleReset = useCallback(() => {
    setAgeValue('');
    setHeight(null);
    setWeight(null);
    setHeightInput('');
    setWeightInput('');
    setHeightUnit('cm');
    setWeightUnit('kg');
    setHasSyndrome(false);
    setShowETTCalculations(false);
  }, []);

  const toggleETTCalculations = useCallback(() => {
    setShowETTCalculations(prev => !prev);
  }, []);

  return {
    // State values
    ageValue,
    height,
    weight,
    heightUnit,
    weightUnit,
    heightInput,
    weightInput,
    hasSyndrome,
    showETTCalculations,
    
    // Handlers
    setAgeValue,
    handleHeightInputChange,
    handleWeightInputChange,
    handleHeightUnitChange,
    handleWeightUnitChange,
    handleReset,
    toggleETTCalculations,
  };
};